import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import bind from 'memoize-bind';
import moment from 'moment-timezone';
import React, { Component } from 'react';

import { Button, Divider } from '@sumologic/yama-ds-components';

import css from './Calendar.module.scss';
import { CalendarDay } from './CalendarDay';
import { CalendarMonth } from './CalendarMonth';
import { CalendarYear } from './CalendarYear';

export interface CalendarState {
  view: 'day' | 'month' | 'year';
  viewValue: Date;
  visible: boolean;
  transitionDirection: 0 | 1 | -1;
  setFocusInDayView: boolean;
}

export interface CalendarProps {
  active?: Date | undefined;
  activeEnd?: Date;
  minDate?: Date;
  maxDate?: Date;
  isRange?: boolean;
  className?: any;
  showToday: boolean;
  onChange: (event: CalendarChangeEvent) => void;
}

export interface CalendarChangeEvent {
  startDate: Date | undefined;
  endDate?: Date | undefined;
}

export class Calendar extends Component<CalendarProps, CalendarState> {
  static defaultProps: Partial<CalendarProps> = {
    isRange: false,
    showToday: true,
  };

  constructor(props: CalendarProps) {
    super(props);

    this.state = {
      view: 'day',
      viewValue: moment().startOf('month').toDate(),
      visible: true,
      transitionDirection: 0,
      setFocusInDayView: false,
    };
  }

  getSnapshotBeforeUpdate(_prevProps: CalendarProps, prevState: CalendarState) {
    const viewValueIsSame =
      prevState.viewValue.valueOf() === this.state.viewValue.valueOf();
    const viewIsSame = prevState.view === this.state.view;

    if (viewValueIsSame && viewIsSame) {
      return {
        viewValue: prevState.viewValue,
      };
    }

    if (!viewIsSame) {
      const { view } = this.state;
      this.setState({ setFocusInDayView: view === 'day' });
    }

    this.setState({ visible: false });

    return null;
  }

  componentDidUpdate(prevProps: CalendarProps) {
    const { active } = this.props;
    const { viewValue, visible } = this.state;

    if (visible === false) {
      this.setState({ visible: true });
    }

    // If the active value changes, update the view value to be
    // the start of the month of the active date
    if (active && active !== prevProps.active && active !== viewValue) {
      this.setState({
        viewValue: moment(active).startOf('month').toDate(),
      });
    }
  }

  onViewChange(view: 'day' | 'month' | 'year') {
    this.setState({
      view,
      transitionDirection: 0,
    });
  }

  onViewValueChange(viewValue: Date) {
    this.setState({
      viewValue: moment(viewValue).startOf('month').toDate(),
    });
  }

  onTodayClick() {
    const { onChange, isRange } = this.props;
    const today = moment().startOf('day').toDate();

    this.onDirectionChange(0);
    this.onViewValueChange(today);

    if (!isRange) {
      onChange({ startDate: today });
    }

    this.onViewChange('day');
  }

  onDirectionChange(transitionDirection: 0 | 1 | -1) {
    this.setState({ transitionDirection });
  }

  render() {
    const { view, viewValue, visible, setFocusInDayView } = this.state;
    const {
      active,
      activeEnd,
      onChange,
      isRange,
      className,
      showToday,
      minDate,
      maxDate,
    } = this.props;

    return (
      <AnimatePresence>
        {visible && (
          <div className={classNames(css.calendar, className)}>
            {view === 'day' && (
              <CalendarDay
                active={active}
                activeEnd={activeEnd}
                isRange={isRange}
                maxDate={maxDate}
                minDate={minDate}
                onChange={onChange}
                onDirectionChange={bind(this.onDirectionChange, this)}
                onViewChange={bind(this.onViewChange, this)}
                onViewValueChange={bind(this.onViewValueChange, this)}
                setFocusInDayView={setFocusInDayView}
                viewValue={viewValue}
              />
            )}
            {view === 'month' && (
              <CalendarMonth
                onViewChange={bind(this.onViewChange, this)}
                onViewValueChange={bind(this.onViewValueChange, this)}
                viewValue={viewValue}
              />
            )}
            {view === 'year' && (
              <CalendarYear
                onDirectionChange={bind(this.onDirectionChange, this)}
                onViewChange={bind(this.onViewChange, this)}
                onViewValueChange={bind(this.onViewValueChange, this)}
                viewValue={viewValue}
              />
            )}
            {showToday && (
              <>
                <Divider />
                <Button onClick={bind(this.onTodayClick, this)} variant='text'>
                  Today
                </Button>
              </>
            )}
          </div>
        )}
      </AnimatePresence>
    );
  }
}
